import { Injectable } from '@angular/core';

export type ReactiveDropdownCacheConfig = {
    readonly namespace: string;
    readonly name: string;
    readonly ttlMs: number;
    readonly endpoint?: unknown;
    readonly params?: Record<string, unknown>;
    readonly userScope?: string | number | null;
    readonly clientScope?: string | number | null;
    readonly appName?: string | null;
};

type ReactiveDropdownCacheEntry = {
    readonly cachedAt: number;
    readonly values: unknown[];
    readonly filtered: unknown[];
};

type MutableDropdownListSnapshot = {
    values: unknown[];
    filtered: unknown[];
};

/** Generic in-memory cache for progressive dropdown data. */
@Injectable({ providedIn: 'root' })
export class ReactiveDropdownCacheService {
    private readonly cache = new Map<string, ReactiveDropdownCacheEntry>();

    public restore(config: ReactiveDropdownCacheConfig, list: unknown): boolean {
        const cached = this.cache.get(this.buildCacheKey(config));

        if (!cached) {
            return false;
        }

        if (Date.now() - cached.cachedAt > config.ttlMs) {
            this.cache.delete(this.buildCacheKey(config));
            return false;
        }

        const target = list as MutableDropdownListSnapshot;
        target.values = [...cached.values];
        target.filtered = [...cached.filtered];

        return true;
    }

    public save(config: ReactiveDropdownCacheConfig, list: unknown): void {
        const source = list as MutableDropdownListSnapshot;

        this.cache.set(this.buildCacheKey(config), {
            cachedAt: Date.now(),
            values: [...(source.values ?? [])],
            filtered: [...(source.filtered ?? [])],
        });
    }

    public clear(config: ReactiveDropdownCacheConfig): void {
        this.cache.delete(this.buildCacheKey(config));
    }

    private buildCacheKey(config: ReactiveDropdownCacheConfig): string {
        return [
            config.namespace,
            config.name,
            this.stringifyEndpoint(config.endpoint ?? config.name),
            String(config.userScope ?? 'all-users'),
            String(config.clientScope ?? 'all-clients'),
            String(config.appName ?? 'all-apps'),
            this.stringifyParams(config.params ?? {}),
        ].join(':');
    }

    private stringifyEndpoint(endpoint: unknown): string {
        if (typeof endpoint === 'string') {
            return endpoint;
        }

        if (endpoint && typeof endpoint === 'object') {
            const requestData = (endpoint as { getRequestData?: () => unknown }).getRequestData?.();

            if (requestData && typeof requestData === 'object') {
                const record = requestData as Record<string, unknown>;

                return String(record['endpoint'] ?? record['url'] ?? this.stringifyParams(record));
            }
        }

        return String(endpoint ?? 'unknown-endpoint');
    }

    private stringifyParams(params: Record<string, unknown>): string {
        return JSON.stringify(this.sortRecord(params));
    }

    private sortRecord(params: Record<string, unknown>): Record<string, unknown> {
        return Object.keys(params)
            .sort()
            .reduce<Record<string, unknown>>((sorted, key) => {
                sorted[key] = params[key];

                return sorted;
            }, {});
    }
}
